import { getMaturaCourseCode } from "./maturaCourseCodes";
export const MATURA_COURSE_PASS_PERCENT = 50;
export const MATURA_COURSE_PRACTICE_STORAGE_KEY = "maturaCoursePracticeVisits";
export const MATURA_COURSE_TARGET_STORAGE_KEY = "maturaCourseTarget";
export const MATURA_COURSE_TARGET_PROGRESS_ID = "matura_course_target";
export const MATURA_COURSE_CUSTOM_TARGET = "custom";
export const MATURA_COURSE_CUSTOM_PLAN_STORAGE_KEY = "maturaCourseCustomPlan";
export const MATURA_COURSE_CUSTOM_PLAN_PROGRESS_ID = "matura_course_custom_plan";
export const MATURA_COURSE_TARGETS = Object.freeze([50, 60, 80, 100]);
export const MATURA_COURSE_DEFAULT_TARGET = 100;

const practiceOwnerKey = (userEmail) => String(userEmail || "guest").trim().toLowerCase();

export function readMaturaCoursePracticeVisits(userEmail) {
  if (typeof window === "undefined") return {};
  try {
    const allVisits = JSON.parse(window.localStorage.getItem(MATURA_COURSE_PRACTICE_STORAGE_KEY) || "{}");
    return allVisits?.[practiceOwnerKey(userEmail)] || {};
  } catch {
    return {};
  }
}

export function markMaturaCoursePracticeVisited(code, userEmail) {
  if (typeof window === "undefined" || !code) return;
  try {
    const allVisits = JSON.parse(window.localStorage.getItem(MATURA_COURSE_PRACTICE_STORAGE_KEY) || "{}");
    const owner = practiceOwnerKey(userEmail);
    allVisits[owner] = {
      ...(allVisits[owner] || {}),
      [String(code)]: new Date().toISOString(),
    };
    window.localStorage.setItem(MATURA_COURSE_PRACTICE_STORAGE_KEY, JSON.stringify(allVisits));
  } catch {
    // Brak localStorage nie blokuje ćwiczeń.
  }
}

export function readMaturaCourseTarget(userEmail) {
  if (typeof window === "undefined") return MATURA_COURSE_DEFAULT_TARGET;
  try {
    const targets = JSON.parse(window.localStorage.getItem(MATURA_COURSE_TARGET_STORAGE_KEY) || "{}");
    const savedTarget = targets?.[practiceOwnerKey(userEmail)];
    if (savedTarget === MATURA_COURSE_CUSTOM_TARGET) return MATURA_COURSE_CUSTOM_TARGET;
    const target = Number(savedTarget);
    return MATURA_COURSE_TARGETS.includes(target) ? target : MATURA_COURSE_DEFAULT_TARGET;
  } catch {
    return MATURA_COURSE_DEFAULT_TARGET;
  }
}

export function saveMaturaCourseTarget(target, userEmail) {
  const normalizedTarget = target === MATURA_COURSE_CUSTOM_TARGET
    ? MATURA_COURSE_CUSTOM_TARGET
    : Number(target);
  if (typeof window === "undefined" || (
    normalizedTarget !== MATURA_COURSE_CUSTOM_TARGET
    && !MATURA_COURSE_TARGETS.includes(normalizedTarget)
  )) return;
  try {
    const targets = JSON.parse(window.localStorage.getItem(MATURA_COURSE_TARGET_STORAGE_KEY) || "{}");
    targets[practiceOwnerKey(userEmail)] = normalizedTarget;
    window.localStorage.setItem(MATURA_COURSE_TARGET_STORAGE_KEY, JSON.stringify(targets));
  } catch {
    // Brak localStorage nie blokuje wyboru zakresu w bieżącej sesji.
  }
}

export function normalizeMaturaCourseCustomPlan(plan) {
  const codes = Array.from(new Set((Array.isArray(plan?.codes) ? plan.codes : [])
    .map((code) => getMaturaCourseCode(String(code || "").trim()))
    .filter(Boolean)));
  return {
    name: String(plan?.name || "Własny zakres").trim().slice(0, 60) || "Własny zakres",
    codes,
  };
}

export function readMaturaCourseCustomPlan(userEmail) {
  if (typeof window === "undefined") return normalizeMaturaCourseCustomPlan(null);
  try {
    const plans = JSON.parse(window.localStorage.getItem(MATURA_COURSE_CUSTOM_PLAN_STORAGE_KEY) || "{}");
    return normalizeMaturaCourseCustomPlan(plans?.[practiceOwnerKey(userEmail)]);
  } catch {
    return normalizeMaturaCourseCustomPlan(null);
  }
}

export function saveMaturaCourseCustomPlan(plan, userEmail) {
  const normalizedPlan = normalizeMaturaCourseCustomPlan(plan);
  if (typeof window === "undefined") return normalizedPlan;
  try {
    const plans = JSON.parse(window.localStorage.getItem(MATURA_COURSE_CUSTOM_PLAN_STORAGE_KEY) || "{}");
    plans[practiceOwnerKey(userEmail)] = normalizedPlan;
    window.localStorage.setItem(MATURA_COURSE_CUSTOM_PLAN_STORAGE_KEY, JSON.stringify(plans));
  } catch {
    // Brak localStorage nie blokuje ustawień bieżącej sesji.
  }
  return normalizedPlan;
}

export function extendMaturaCourseCustomPlan({
  currentTarget,
  customPlan,
  targetCodes,
  topicCode,
}) {
  const baseCodes = currentTarget === MATURA_COURSE_CUSTOM_TARGET
    ? customPlan?.codes
    : Array.from(targetCodes || []);
  return normalizeMaturaCourseCustomPlan({
    name: currentTarget === MATURA_COURSE_CUSTOM_TARGET
      ? customPlan?.name
      : `Mój zakres ${currentTarget}%+`,
    codes: [...(Array.isArray(baseCodes) ? baseCodes : []), topicCode],
  });
}

export function normalizeMaturaCoursePercent(value) {
  if (!Number.isFinite(value)) return null;
  return Math.round(value <= 1 ? value * 100 : value);
}

export function isMaturaCourseTopicPassed(progress) {
  const percent = normalizeMaturaCoursePercent(
    progress?.bestScore ?? progress?.score
  );
  return percent !== null && percent >= MATURA_COURSE_PASS_PERCENT;
}
